import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import { getUserInfoFromToken } from "../services/auth";
import PageHeader from "../components/common/PageHeader";
import MessageFilter from "../components/MessageFilter";
import LoadingSpinner from "../components/LoadingSpinner";
import Pagination from "../components/Pagination";

const MessageHistory = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [selectedErrorMessage, setSelectedErrorMessage] = useState(null);

  const messagesPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMessageHistory = async () => {
      const { userId } = getUserInfoFromToken();
      if (!userId) return alert("User ID not found in token");

      try {
        const res = await api.get(`/messages/history/by-user`);
        if (
          res.data?.MessageHistory &&
          Array.isArray(res.data.MessageHistory)
        ) {
          setMessages(res.data.MessageHistory);
        } else {
          console.error("Unexpected response format:", res.data);
          setMessages([]);
        }
      } catch (error) {
        console.error("Error fetching message history:", error);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessageHistory();
  }, []);

  const filteredMessages = messages.filter((msg) => {
    const msgDate = new Date(msg.sentAt).setHours(0, 0, 0, 0);
    const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
    const to = toDate ? new Date(toDate).setHours(0, 0, 0, 0) : null;

    // if (msg.status !== "sent") return false;

    if (from && msgDate < from) return false;
    if (to && msgDate > to) return false;

    return true;
  });

  const sortedMessages = React.useMemo(() => {
    if (!sortConfig.key) return filteredMessages;

    return [...filteredMessages].sort((a, b) => {
      const aValue =
        sortConfig.key === "name"
          ? a.Customer?.name || ""
          : sortConfig.key === "phone"
          ? a.Customer?.phoneNumber || ""
          : sortConfig.key === "status"
          ? a.status
          : sortConfig.key === "templateName"
          ? a.templateName || ""
          : new Date(a.sentAt);

      const bValue =
        sortConfig.key === "name"
          ? b.Customer?.name || ""
          : sortConfig.key === "phone"
          ? b.Customer?.phoneNumber || ""
          : sortConfig.key === "status"
          ? b.status
          : sortConfig.key === "templateName"
          ? b.templateName || ""
          : new Date(b.sentAt);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredMessages, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const totalSentMessages = sortedMessages.length;
  const indexOfLastMessage = currentPage * messagesPerPage;
  const indexOfFirstMessage = indexOfLastMessage - messagesPerPage;
  const currentMessages = sortedMessages.slice(
    indexOfFirstMessage,
    indexOfLastMessage
  );
  const totalPages = Math.ceil(sortedMessages.length / messagesPerPage);

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <PageHeader
        title="Message History"
        subtitle="Overview of all sent messages and their delivery status."
      />

      <MessageFilter
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        onClear={() => {
          setFromDate("");
          setToDate("");
        }}
        totalMessages={totalSentMessages}
      />

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <LoadingSpinner
            fullScreen={true}
            size="lg"
            text="Loading messages..."
          />
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block lg:hidden">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {currentMessages.length} Message
                  {currentMessages.length !== 1 ? "s" : ""}
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {currentMessages.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <p className="text-gray-500">No messages found.</p>
                  </div>
                ) : (
                  currentMessages.map((message, index) => (
                    <div key={message.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">
                            {message.Customer?.name || "N/A"}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {message.Customer?.phoneNumber || "N/A"}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Template:{" "}
                            {message.MetaTemplate?.templateName || "N/A"}
                          </p>
                        </div>
                        <span
                          onClick={() => {
                            if (message.status === "failed" && message.error) {
                              setSelectedErrorMessage(message);
                            }
                          }}
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
                            message.status === "sent"
                              ? "bg-green-100 text-green-800"
                              : message.status === "failed"
                              ? "bg-red-100 text-red-800 cursor-pointer hover:bg-red-200 transition-colors"
                              : message.status === "read"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {message.status || "Pending"}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(message.sentAt).toLocaleDateString("en-US", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto overflow-y-visible">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("name")}
                    >
                      Name {getSortIndicator("name")}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("phone")}
                    >
                      Phone {getSortIndicator("phone")}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("status")}
                    >
                      Status {getSortIndicator("status")}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("templateName")}
                    >
                      Template {getSortIndicator("templateName")}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("sentAt")}
                    >
                      Sent At {getSortIndicator("sentAt")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentMessages.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-4 py-12 text-center text-gray-500"
                      >
                        No messages found.
                      </td>
                    </tr>
                  ) : (
                    currentMessages.map((message, index) => (
                      <tr
                        key={message.id}
                        className="hover:bg-gray-50 transition duration-150"
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {indexOfFirstMessage + index + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {message.Customer?.name || "N/A"}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {message.Customer?.phoneNumber || "N/A"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            onClick={() => {
                              if (message.status === "failed" && message.error) {
                                setSelectedErrorMessage(message);
                              }
                            }}
                            className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
                              message.status === "sent"
                                ? "bg-green-100 text-green-800 cursor-default"
                                : message.status === "failed"
                                ? "bg-red-100 text-red-800 cursor-pointer hover:bg-red-200 transition-colors"
                                : message.status === "read"
                                ? "bg-blue-100 text-blue-800 cursor-default"
                                : "bg-yellow-100 text-yellow-800 cursor-default"
                            }`}
                          >
                            {message.status || "Pending"}
                          </span>
                        </td>

                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {message.MetaTemplate?.templateName || "N/A"}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(message.sentAt).toLocaleString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {sortedMessages.length > messagesPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Error Message Modal (Mobile Only) */}
      {selectedErrorMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Message Failed
              </h3>
              <button
                onClick={() => setSelectedErrorMessage(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Customer:</span>{" "}
                {selectedErrorMessage.Customer?.name || "N/A"}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-medium">Phone:</span>{" "}
                {selectedErrorMessage.Customer?.phoneNumber || "N/A"}
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 mb-1">
                Error Details:
              </p>
              <p className="text-sm text-red-700">
                {selectedErrorMessage.error}
              </p>
            </div>
            <button
              onClick={() => setSelectedErrorMessage(null)}
              className="mt-4 w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageHistory;
